import React, { useEffect, useState } from 'react';
//CarbonComponents
import {
  CheckmarkFilled16,
  InProgress16,
  Zip16,
  View16
} from '@carbon/icons-react';
import {
  Accordion,
  AccordionItem,
  Button,
  Link,
  SkeletonText,
  InlineLoading
} from 'carbon-components-react';
import _ from 'lodash';
import { ViewingEmail } from 'types';
import { upperFirst } from 'utilities/helper.util';
import { phoneNumberFormatter } from 'utilities/validation';
import UtilityIcon from 'components/UtilityIcons';
import { selectListAnnouncements } from 'redux/announcement/selectors';
import { useSelector } from 'react-redux';

interface Props {
  assetOwner: any[];
  isLoading?: boolean;
  handleDownloadPartialZip: Function;
  isDownloadingPartialZip?: boolean;
  buttonClickedId?: number;
  handleEmailOpen: (email: ViewingEmail) => void;
}

export const AssetOwnerList: React.FC<Props> = ({
  assetOwner,
  isLoading,
  handleDownloadPartialZip,
  isDownloadingPartialZip,
  buttonClickedId,
  handleEmailOpen
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const listAnnouncements = useSelector(selectListAnnouncements);
  useEffect(() => {
    const entries = assetOwner
      ?.map((item: any) => {
        const {
          status,
          seq_no,
          id,
          phone,
          isLoading,
          email,
          authority_name,
          statistic,
          Mailbody,
          organisation_type,
          Directattachment,
          enquiry_id,
          sentinel_id
        } = item;

        const announcements: any[] = [];
        if (_.isArray(listAnnouncements) && !_.isEmpty(listAnnouncements)) {
          listAnnouncements?.forEach((announcement: { sentinel_id: any }) => {
            if (announcement.sentinel_id === sentinel_id) {
              announcements.push(announcement);
            }
          });
        }

        const icon = organisation_type || 'other';

        return {
          id,
          icon,
          seq_no,
          phone:
            phone?.length > 0
              ? phoneNumberFormatter({
                  phoneNumber: phone[0]
                }).getNationalNumberFormat()
              : 'N/A',
          email: email || 'N/A',
          assetOwnerName: authority_name,
          mailbodies: Mailbody,
          status: status,
          uploadedFiles: Directattachment,
          typicalWaitTime: statistic?.response_time,
          enquiryId: enquiry_id,
          isLoading,
          notices: announcements
        };
      })
      .sort((itemA: any, itemB: any) =>
        itemA.assetOwnerName.localeCompare(itemB.assetOwnerName)
      );
    setRows(entries);
  }, [assetOwner, listAnnouncements]);

  return (
    <>
      {!isLoading && (
        <Accordion>
          {rows.map((row: any, idx: any) => {
            return (
              <section
                key={idx}
                className={
                  row.status !== 'received' &&
                  row.notices &&
                  row.notices.length > 0
                    ? 'accordion-announcement-active'
                    : ''
                }
              >
                <AccordionItem
                  title={
                    <div className="accordion-title">
                      <div className="accordion-title-left-side">
                        <div className="icon-div">
                          <UtilityIcon type={row.icon} />
                        </div>
                        <div className="title-name">
                          {row.mailbodies?.length > 1
                            ? `(${row.mailbodies.length}) `
                            : ''}
                          {row.assetOwnerName}
                        </div>
                      </div>
                      <div className="status-div">
                        {row.status === 'received' ? (
                          <CheckmarkFilled16 className="status-received" />
                        ) : (
                          <InProgress16 className="status-others" />
                        )}
                      </div>
                    </div>
                  }
                >
                  <section className="accordion-content">
                    <section className="asset-owner-information">
                      {row.isLoading ? (
                        <>
                          <SkeletonText />
                          <SkeletonText />
                          <SkeletonText />
                          <SkeletonText />
                          <SkeletonText />
                        </>
                      ) : (
                        <>
                          <div>
                            <span className="info-title">Referral</span>
                            &nbsp;
                            <span>{row.seq_no}</span>
                          </div>
                          <div>
                            <span className="info-title">Status</span>
                            &nbsp;
                            <span>
                              {row.status ? upperFirst(row.status) : ''}
                            </span>
                          </div>
                          {row.status !== 'received' && (
                            <div>
                              <span className="info-title">Typical wait</span>
                              &nbsp;
                              <span>
                                {(row.typicalWaitTime &&
                                  upperFirst(row.typicalWaitTime)) ||
                                  'Not enough data available'}
                              </span>
                            </div>
                          )}
                          <div>
                            <span className="info-title">Phone</span>
                            &nbsp;
                            {row.phone !== 'N/A' ? (
                              <Link href={`tel:${row.phone}`}>{row.phone}</Link>
                            ) : (
                              <span>{row.phone}</span>
                            )}
                          </div>
                          <div>
                            <span className="info-title">Email</span>
                            &nbsp;
                            <span>{row.email}</span>
                          </div>
                        </>
                      )}
                    </section>
                    {row.status === 'received' &&
                      (row.mailbodies?.length > 0 ||
                        row.uploadedFiles?.length > 0) && (
                        <section className="asset-owner-button-section">
                          {row.mailbodies.length > 0 && (
                            <Button
                              kind="tertiary"
                              size="field"
                              renderIcon={View16}
                              className="response-button"
                              onClick={() =>
                                handleEmailOpen({
                                  seqNo: row.seq_no,
                                  assetOwnerContact:
                                    row.phone !== 'N/A' ? row.phone : undefined,
                                  id: row.id,
                                  enquiryId: row.enquiryId,
                                  name: row.assetOwnerName,
                                  bodies: row.mailbodies,
                                  uploadedFiles: row.uploadedFiles
                                })
                              }
                            >
                              Response{row.mailbodies.length > 1 ? 's' : ''}
                            </Button>
                          )}
                          <Button
                            key={'download-zip-btn'}
                            kind={'tertiary'}
                            size="field"
                            renderIcon={
                              isDownloadingPartialZip &&
                              buttonClickedId === row.id
                                ? ''
                                : Zip16
                            }
                            onClick={() =>
                              handleDownloadPartialZip(
                                row.id,
                                row.seq_no,
                                row.authorityName
                              )
                            }
                            disabled={isDownloadingPartialZip}
                            className="download-zip-button"
                          >
                            {!isDownloadingPartialZip ? (
                              'ZIP file'
                            ) : (
                              <>
                                {isDownloadingPartialZip &&
                                buttonClickedId === row.id ? (
                                  <InlineLoading
                                    description={
                                      isDownloadingPartialZip
                                        ? 'Downloading...'
                                        : 'ZIP file'
                                    }
                                    aria-live={'assertive'}
                                  />
                                ) : (
                                  'ZIP file'
                                )}
                              </>
                            )}
                          </Button>
                        </section>
                      )}
                  </section>
                </AccordionItem>
                {row.status !== 'received' &&
                  row.notices &&
                  row.notices.length > 0 && (
                    <section className="has-announcement-td-container">
                      <div className="member-announcement">
                        <div className="member-announcement-header">
                          MEMBER ANNOUNCEMENT
                        </div>
                        <div className="member-announcement-wrapper">
                          {row.notices.map((announcement: any) => {
                            return (
                              <>
                                <div className="member-announcement-title">
                                  {announcement.title}
                                </div>
                                <div
                                  className="member-announcement-body"
                                  dangerouslySetInnerHTML={{
                                    __html: announcement.text
                                  }}
                                />
                              </>
                            );
                          })}
                        </div>
                      </div>
                    </section>
                  )}
              </section>
            );
          })}
        </Accordion>
      )}
    </>
  );
};

export default React.memo(AssetOwnerList);
